// Notes: notebooks, links to projects/tasks, smart views, search, trash (docs/plans/notes.md §3).
// A note is two docs: `notes/{id}` (metadata + a plain-text copy, always in memory) and
// `notes/{id}/chunks/body` ({ html, rev }, loaded when the note is opened). Pure — no I/O.

export const INBOX_ID = 'inbox';
export const TRASH_DAYS = 30;
export const TEXT_MAX = 4000;          // plain-text copy kept on the metadata doc (search, previews, Daily Log)
const DAY_MS = 86400000;

// System notebooks — fixed ids, so two devices creating them at once write the same docs.
export const SYSTEM_NOTEBOOKS = [
  { id: INBOX_ID, name: 'Inbox', icon: '📥', color: '#FB5446', sortOrder: 0, system: true },
  { id: 'daily-logs', name: 'Daily logs', icon: '📅', color: '#16B47A', sortOrder: 1, system: true, forKind: 'day' },
  { id: 'week-reviews', name: 'Week reviews', icon: '🗓️', color: '#4F8EF7', sortOrder: 2, system: true, forKind: 'week' },
  { id: 'templates', name: 'Templates', icon: '🧩', color: '#B07CF7', sortOrder: 3, system: true, forKind: 'template' },
];
export const TEMPLATES_ID = 'templates';
export const notebookForKind = (kind) => SYSTEM_NOTEBOOKS.find((n) => n.forKind === kind)?.id || INBOX_ID;

// ---- text / html ------------------------------------------------------------------------

const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ENT[c]);

/** Plain text → HTML paragraphs (blank lines split paragraphs, single newlines become <br>). */
export function textToHtml(text) {
  const t = String(text ?? '').replace(/\r\n?/g, '\n').trim();
  if (!t) return '';
  return t.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('');
}

/**
 * HTML → readable plain text (block ends become newlines, entities decoded). `paragraphs` keeps a
 * blank line between paragraphs so textToHtml(htmlToText(h, { paragraphs: true })) round-trips —
 * the plain editor uses that; previews and search use the compact form.
 */
export function htmlToText(html, { paragraphs = false } = {}) {
  const t = String(html ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<\/(p|h[1-6]|blockquote|pre|ul|ol|table)>/gi, paragraphs ? '\n\n' : '\n')
    .replace(/<\/(div|li|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/[ \t]+\n/g, '\n');
  return (paragraphs ? t.replace(/\n{3,}/g, '\n\n') : t.replace(/\n{2,}/g, '\n')).trim();
}

/**
 * Links a body makes to the rest of the app: mentions (`ops://project/ID`, `ops://task/ID`) and note links
 * (`ops://note/ID`). → { projects, tasks, notes } (unique ids, in order).
 */
export function extractRefs(html) {
  const out = { projects: [], tasks: [], notes: [] };
  const key = { project: 'projects', task: 'tasks', note: 'notes' };
  const re = /href="ops:\/\/(project|task|note)\/([A-Za-z0-9_-]+)"/g;
  let m;
  while ((m = re.exec(String(html || '')))) if (!out[key[m[1]]].includes(m[2])) out[key[m[1]]].push(m[2]);
  return out;
}
export const opsHref = (kind, id) => `ops://${kind}/${id}`;
export function parseOpsHref(href) {
  const m = /^ops:\/\/(project|task|note)\/([A-Za-z0-9_-]+)$/.exec(String(href || ''));
  return m ? { kind: m[1], id: m[2] } : null;
}

/** Notes that link to `noteId` (their `refs`), newest first. */
export const backlinksTo = (notes, noteId) => (notes || []).filter((n) => !isTrashed(n) && n.id !== noteId && (n.refs || []).includes(noteId))
  .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

/** Fill a template's {{date}} {{time}} {{project}} {{task}} {{title}} placeholders (html-escaped). */
export function fillTemplate(html, vars = {}) {
  return String(html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (all, k) => (vars[k] != null ? escapeHtml(vars[k]) : ''));
}

export const wordCount = (text) => (String(text ?? '').match(/\S+/g) || []).filter((w) => w !== '•').length;

/** The metadata fields derived from a body. */
export function bodyMeta(html) {
  const text = htmlToText(html);
  return {
    content: text.slice(0, TEXT_MAX),
    words: wordCount(text),
    bodyBytes: String(html ?? '').length,
    hasChecklist: /data-type="taskList"|<input[^>]*checkbox/i.test(html || ''),
    hasImages: /<img\b/i.test(html || ''),
    refs: extractRefs(html).notes,
  };
}

// ---- links ------------------------------------------------------------------------------

const linkKey = (l) => `${l.type}:${l.id}`;

/** A note's links, including the legacy single projectId / taskId fields. */
export function noteLinks(note) {
  const out = []; const seen = new Set();
  const add = (l) => { if (l && l.id && (l.type === 'project' || l.type === 'task') && !seen.has(linkKey(l))) { seen.add(linkKey(l)); out.push({ type: l.type, id: l.id }); } };
  (Array.isArray(note?.links) ? note.links : []).forEach(add);
  if (note?.projectId) add({ type: 'project', id: note.projectId });
  if (note?.taskId) add({ type: 'task', id: note.taskId });
  return out;
}

/** Fields to write for a set of links: the array + the first project/task kept for older readers. */
export function linksPatch(links) {
  const clean = noteLinks({ links });
  return {
    links: clean,
    projectId: clean.find((l) => l.type === 'project')?.id || null,
    taskId: clean.find((l) => l.type === 'task')?.id || null,
  };
}

/** Is the note pinned to this project (directly, or through one of its tasks)? */
export function noteIsForProject(note, projectId, tasksById) {
  return noteLinks(note).some((l) => (l.type === 'project' && l.id === projectId)
    || (l.type === 'task' && tasksById?.get(l.id)?.projectId === projectId));
}
export const noteIsForTask = (note, taskId) => noteLinks(note).some((l) => l.type === 'task' && l.id === taskId);

// ---- migration --------------------------------------------------------------------------

/** A note from before notebooks (plain `content`, no body chunk) → { patch, body } or null if current. */
export function migrateNote(note) {
  if (!note || note.bodyRev != null) return null;
  const html = textToHtml(note.content || '');
  return {
    patch: {
      notebookId: note.notebookId || notebookForKind(note.kind),
      ...linksPatch(noteLinks(note)),
      tags: Array.isArray(note.tags) ? note.tags : [],
      deletedAt: note.deletedAt ?? null,
      bodyRev: 1,
      ...bodyMeta(html),
    },
    body: { html, rev: 1 },
  };
}

// ---- notebooks --------------------------------------------------------------------------

/** Notebooks as a depth-first list: [{ nb, depth, children }], system first then sortOrder/name. */
export function notebookTree(notebooks, { includeArchived = false } = {}) {
  const live = (notebooks || []).filter((n) => includeArchived || !n.archivedAt);
  const ids = new Set(live.map((n) => n.id));
  const kids = new Map();
  for (const n of live) {
    const parent = n.parentId && ids.has(n.parentId) && n.parentId !== n.id ? n.parentId : null;
    if (!kids.has(parent)) kids.set(parent, []);
    kids.get(parent).push(n);
  }
  const order = (a, b) => (b.system ? 1 : 0) - (a.system ? 1 : 0) || (a.sortOrder ?? 1e9) - (b.sortOrder ?? 1e9) || String(a.name).localeCompare(String(b.name));
  const out = []; const seen = new Set();
  const walk = (parent, depth) => {
    for (const nb of (kids.get(parent) || []).sort(order)) {
      if (seen.has(nb.id)) continue;           // a parent loop can't recurse forever
      seen.add(nb.id);
      out.push({ nb, depth, children: (kids.get(nb.id) || []).length });
      walk(nb.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** A notebook and every notebook under it. */
export function notebookAndDescendants(notebooks, id) {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const n of notebooks || []) if (n.parentId && out.has(n.parentId) && !out.has(n.id)) { out.add(n.id); grew = true; }
  }
  return out;
}

/** The notebooks from the top level down to `id` (inclusive). A parent loop or a missing parent stops the walk. */
export function notebookPath(notebooks, id) {
  const byId = new Map((notebooks || []).map((n) => [n.id, n]));
  const out = []; const seen = new Set();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) { seen.add(cur.id); out.unshift(cur); cur = cur.parentId ? byId.get(cur.parentId) : null; }
  return out;
}

/**
 * Per notebook, folder-style: { notes: in it directly, total: in it and every notebook under it, folders: live
 * notebooks directly inside }. Trashed notes and archived notebooks don't count.
 */
export function notebookCounts(notebooks, notes) {
  const live = (notebooks || []).filter((n) => !n.archivedAt);
  const direct = new Map();
  for (const n of notes || []) if (!n.deletedAt) direct.set(n.notebookId, (direct.get(n.notebookId) || 0) + 1);
  const out = new Map();
  for (const nb of live) {
    let total = 0;
    for (const id of notebookAndDescendants(live, nb.id)) total += direct.get(id) || 0;
    out.set(nb.id, { notes: direct.get(nb.id) || 0, total, folders: live.filter((c) => c.parentId === nb.id && c.id !== nb.id).length });
  }
  return out;
}

// ---- sections (named groups inside one notebook, docs/plans/trilium-import.md §2) --------
// They live on the notebook doc as `sections: [{ id, name, sortOrder }]`; a note points at one with `sectionId`
// (null / unknown = unsectioned). Not a notebook level — no privacy, no nesting.

export const SECTION_NAME_MAX = 80;
const cleanSectionName = (name) => String(name ?? '').trim().slice(0, SECTION_NAME_MAX);

/** The notebook's sections in order. */
export const notebookSections = (nb) => [...(Array.isArray(nb?.sections) ? nb.sections : [])]
  .filter((x) => x && x.id)
  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name)));

/** A new sections array with `name` added at the end. */
export function sectionsAdd(sections, { id, name }) {
  const nm = cleanSectionName(name);
  if (!nm) throw new Error('section name required');
  if (!id) throw new Error('section id required');
  const list = notebookSections({ sections });
  if (list.some((x) => x.name.toLowerCase() === nm.toLowerCase())) throw new Error(`there's already a section called ${nm}`);
  const last = list.length ? list[list.length - 1].sortOrder ?? list.length : 0;
  return [...list, { id, name: nm, sortOrder: last + 1000 }];
}

export function sectionsRename(sections, id, name) {
  const nm = cleanSectionName(name);
  if (!nm) throw new Error('section name required');
  const list = notebookSections({ sections });
  if (!list.some((x) => x.id === id)) throw new Error(`no section ${id}`);
  if (list.some((x) => x.id !== id && x.name.toLowerCase() === nm.toLowerCase())) throw new Error(`there's already a section called ${nm}`);
  return list.map((x) => (x.id === id ? { ...x, name: nm } : x));
}

/** Swap with the neighbour above (-1) or below (+1); sortOrders are renumbered so they stay distinct. */
export function sectionsMove(sections, id, dir) {
  const list = notebookSections({ sections });
  const i = list.findIndex((x) => x.id === id);
  if (i < 0) throw new Error(`no section ${id}`);
  const j = i + (dir < 0 ? -1 : 1);
  if (j < 0 || j >= list.length) return list;
  [list[i], list[j]] = [list[j], list[i]];
  return list.map((x, k) => ({ ...x, sortOrder: (k + 1) * 1000 }));
}

export const sectionsRemove = (sections, id) => notebookSections({ sections }).filter((x) => x.id !== id);

/** Find a section of a notebook by id or name (case-insensitive). */
export function findSection(nb, ref) {
  const r = String(ref ?? '').trim().toLowerCase();
  if (!r) return null;
  const list = notebookSections(nb);
  return list.find((x) => x.id === ref) || list.find((x) => x.name.toLowerCase() === r) || null;
}

/**
 * A notebook's own notes grouped for its page: unsectioned first (section null), then each section in order —
 * empty sections included so they can be filled. Notes keep the order they came in (already sorted by the caller).
 */
export function groupNotesBySection(notes, nb) {
  const sections = notebookSections(nb);
  const ids = new Set(sections.map((x) => x.id));
  const out = [{ section: null, notes: [] }, ...sections.map((section) => ({ section, notes: [] }))];
  const at = new Map(out.map((g, i) => [g.section?.id ?? null, i]));
  for (const n of notes || []) out[at.get(ids.has(n.sectionId) ? n.sectionId : null)].notes.push(n);
  return out;
}

// ---- views, search, trash ---------------------------------------------------------------

export const isTrashed = (n) => !!n?.deletedAt;
export const trashExpired = (n, now = Date.now()) => isTrashed(n) && now - n.deletedAt > TRASH_DAYS * DAY_MS;

/** Every word of the query appears in the title, text or tags. */
export function matchesQuery(note, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${note.title || ''}\n${note.content || ''}\n${(note.tags || []).join(' ')}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

export const SORTS = {
  created: (a, b) => (b.createdAt || b.updatedAt || 0) - (a.createdAt || a.updatedAt || 0),
  date: (a, b) => String(b.date || '').localeCompare(String(a.date || '')) || (b.updatedAt || 0) - (a.updatedAt || 0),
  updated: (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0),
  title: (a, b) => String(a.title || '').localeCompare(String(b.title || '')),
};
/** The note-list orders people can pick, in menu order. Newest note first is the default everywhere. */
export const NOTE_SORTS = [['created', 'Newest'], ['updated', 'Last edited'], ['date', 'Date'], ['title', 'Title']];
export const DEFAULT_NOTE_SORT = 'created';

/**
 * Notes for a view. `view`: 'all' | 'today' | 'pinned' | 'recent' | 'trash'; or pass notebookId
 * (with sub-notebooks when `deep`), projectId (+ tasksById for notes pinned to its tasks), taskId.
 * Newest first unless `sort` says otherwise. Pinned notes keep their place (they have their own Pinned view).
 */
export function selectNotes(notes, { view = 'all', notebookId, notebooks, deep = false, projectId, taskId, tasksById, query, today, sort = DEFAULT_NOTE_SORT, now = Date.now() } = {}) {
  const templates = notebookId === TEMPLATES_ID;
  let list = (notes || []).filter((n) => (view === 'trash' ? isTrashed(n) : !isTrashed(n)) && (templates || n.kind !== 'template'));
  if (view === 'today') list = list.filter((n) => n.date === today);
  if (view === 'pinned') list = list.filter((n) => n.pinned);
  if (view === 'recent') list = list.filter((n) => now - (n.updatedAt || 0) < 7 * DAY_MS);
  if (notebookId) {
    const ids = deep ? notebookAndDescendants(notebooks, notebookId) : new Set([notebookId]);
    list = list.filter((n) => ids.has(n.notebookId || INBOX_ID));
  }
  if (projectId) list = list.filter((n) => noteIsForProject(n, projectId, tasksById));
  if (taskId) list = list.filter((n) => noteIsForTask(n, taskId));
  if (query) list = list.filter((n) => matchesQuery(n, query));
  const by = view === 'trash' ? (a, b) => (b.deletedAt || 0) - (a.deletedAt || 0) : SORTS[sort] || SORTS[DEFAULT_NOTE_SORT];
  return [...list].sort(by);
}

/** Counts for the Notes home: smart views, per notebook (own notes only), per project. */
export function noteCounts(notes, { today, now = Date.now(), tasksById } = {}) {
  const c = { all: 0, today: 0, pinned: 0, recent: 0, trash: 0, byNotebook: {}, byProject: {} };
  for (const n of notes || []) {
    if (n.kind === 'template') { if (!isTrashed(n)) c.byNotebook[TEMPLATES_ID] = (c.byNotebook[TEMPLATES_ID] || 0) + 1; continue; }
    if (isTrashed(n)) { c.trash++; continue; }
    c.all++;
    if (n.date === today) c.today++;
    if (n.pinned) c.pinned++;
    if (now - (n.updatedAt || 0) < 7 * DAY_MS) c.recent++;
    const nb = n.notebookId || INBOX_ID;
    c.byNotebook[nb] = (c.byNotebook[nb] || 0) + 1;
    const projects = new Set();
    for (const l of noteLinks(n)) {
      if (l.type === 'project') projects.add(l.id);
      else { const pid = tasksById?.get(l.id)?.projectId; if (pid) projects.add(pid); }
    }
    for (const pid of projects) c.byProject[pid] = (c.byProject[pid] || 0) + 1;
  }
  return c;
}

/** Tags in use, most used first. */
export function allTags(notes) {
  const m = new Map();
  for (const n of notes || []) if (!isTrashed(n)) for (const t of n.tags || []) m.set(t, (m.get(t) || 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

export const normalizeTag = (t) => String(t || '').trim().replace(/^#/, '').replace(/\s+/g, '-').toLowerCase().slice(0, 40);

// ── Note activity ─────────────────────────────────────────────────────────────────────────────────────────────
// When a note was worked on: one row per local day { day, first, last, saves } on the note's meta (`activity`), newest
// first, capped. Written with the body save (no extra write). Kept small and plain so a later, bigger activity feature
// (who / which device / what changed) can read it or migrate it.
export const NOTE_ACTIVITY_MAX_DAYS = 366;

/** activity + one save at `now` on local day `day` → the new activity array (input untouched). */
export function recordNoteActivity(activity, { now = Date.now(), day } = {}) {
  const d = day || localDayOf(now);
  const rows = Array.isArray(activity) ? activity.filter((r) => r && r.day) : [];
  const i = rows.findIndex((r) => r.day === d);
  const next = i >= 0
    ? rows.map((r, k) => (k === i ? { ...r, first: Math.min(r.first || now, now), last: Math.max(r.last || 0, now), saves: (r.saves || 0) + 1 } : r))
    : [{ day: d, first: now, last: now, saves: 1 }, ...rows];
  return next.sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0)).slice(0, NOTE_ACTIVITY_MAX_DAYS);
}

/** Summary for a note header: { days, lastAt, firstDay } (days = how many days it was worked on). */
export function noteActivitySummary(activity) {
  const rows = Array.isArray(activity) ? activity.filter((r) => r && r.day) : [];
  if (!rows.length) return { days: 0, lastAt: null, firstDay: null };
  return { days: rows.length, lastAt: Math.max(...rows.map((r) => r.last || 0)) || null, firstDay: rows[rows.length - 1].day };
}

function localDayOf(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Personal + private notebooks (docs/SYNC.md §3.7) ───────────────────────────────────────────────────────────────
// Everyone has their own notebook (`me-<uid>`, "<First>'s Notebook", private) that new notes go into. The old default
// notebooks became tags: Inbox → just the personal notebook; Daily logs / Week reviews → #daily-log / #week-review.
// A notebook is private (`visibility: 'private'` — only its owner and their assistant sync it) or shared with the team
// (the default); a note carries `private` to match the notebook it's in.
export const personalNotebookId = (uid) => `me-${uid}`;
export const LEGACY_NOTEBOOK_TAG = { [INBOX_ID]: null, 'daily-logs': 'daily-log', 'week-reviews': 'week-review' };
export const KIND_TAG = { day: 'daily-log', week: 'week-review' };
export const isLegacyNotebook = (id) => !id || Object.prototype.hasOwnProperty.call(LEGACY_NOTEBOOK_TAG, id);
export const isPrivateNotebook = (nb) => notebookAccess(nb).mode === 'private';

/** The personal notebook doc for someone. */
export function personalNotebookDoc(uid, name = '') {
  const first = String(name || '').trim().split(/\s+/)[0];
  return { id: personalNotebookId(uid), name: first ? `${first}'s Notebook` : 'My Notebook', icon: '📓', color: '#FB5446', visibility: 'private', access: { ...PRIVATE_ACCESS }, personal: true, parentId: null, archivedAt: null, sortOrder: -1 };
}

/**
 * Where a note really goes and what it carries: a legacy default (Inbox, Daily logs, Week reviews, none) → the personal
 * notebook plus that notebook's / kind's tag; `private` follows the notebook. → { notebookId, tags, private }.
 */
export function placeNote({ notebookId, kind, tags = [] }, { notebooks = [], personalId }) {
  let nb = notebookId;
  const add = [];
  if (isLegacyNotebook(nb) && personalId) {
    if (nb && LEGACY_NOTEBOOK_TAG[nb]) add.push(LEGACY_NOTEBOOK_TAG[nb]);
    nb = personalId;
  }
  if (KIND_TAG[kind]) add.push(KIND_TAG[kind]);
  const book = notebooks.find((b) => b.id === nb);
  const priv = book ? isPrivateNotebook(book) : nb === personalId;   // a personal notebook not synced yet is private
  const out = [...(tags || [])];
  for (const t of add) if (!out.includes(t)) out.push(t);
  return { notebookId: nb || INBOX_ID, tags: out, private: priv };
}

// ── Access by department, role and person (docs/SYNC.md §3.10) ────────────────────────────────────────────────────
// Lj (Oct 11): "control access through departments, roles, and individual people — 3 levels all working together."
// notebook.access / note.access = { mode: 'private' | 'team' | 'custom', departments, roles, people }. Custom = anyone in
// one of the departments, OR with one of the roles, OR named — plus the owner (and their assistant), always. A note's
// access is an override (null = its notebook's). `visibility: 'private'` (§3.7) reads as mode private; writers keep it
// as a mirror so an API without §3.10 still enforces private. The server (functions/src/noteaccess.ts) is the same rule.
export const ACCESS_MODES = ['private', 'team', 'custom'];
const ACCESS_LIST_MAX = 200;
const idList = (v) => (Array.isArray(v) ? [...new Set(v.filter((x) => typeof x === 'string' && x).map((x) => x.slice(0, 128)))].slice(0, ACCESS_LIST_MAX) : []);
export const TEAM_ACCESS = Object.freeze({ mode: 'team', departments: [], roles: [], people: [] });
export const PRIVATE_ACCESS = Object.freeze({ mode: 'private', departments: [], roles: [], people: [] });

/** A clean access, or null when `raw` isn't one. Lists only matter for custom (kept empty otherwise). */
export function cleanAccess(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || !ACCESS_MODES.includes(raw.mode)) return null;
  if (raw.mode !== 'custom') return raw.mode === 'private' ? { ...PRIVATE_ACCESS } : { ...TEAM_ACCESS };
  return { mode: 'custom', departments: idList(raw.departments), roles: idList(raw.roles), people: idList(raw.people) };
}

/** A notebook's access: its `access`, else the legacy `visibility`. `visibility: 'private'` always wins — an older
 *  build that only knows the toggle can still make a notebook private (never wider than §3.7). */
export function notebookAccess(nb) {
  if (nb?.visibility === 'private') return { ...PRIVATE_ACCESS };
  return cleanAccess(nb?.access) ?? { ...TEAM_ACCESS };
}

/** A note's own override, or null (= its notebook's). */
export const noteAccessOverride = (note) => cleanAccess(note?.access);

/**
 * A note's resolved access: { ...access, inherited, owners } — owners always see it (the note's owner; for an inherited
 * set also the notebook's). A stale `private: true` under a team notebook still hides it (never wider than §3.7).
 */
export function resolveNoteAccess(note, nb) {
  const own = noteAccessOverride(note);
  if (own) return { ...own, inherited: false, owners: [note?.ownerUid].filter(Boolean) };
  let base = nb ? notebookAccess(nb) : note?.private === true ? { ...PRIVATE_ACCESS } : { ...TEAM_ACCESS };
  if (base.mode === 'team' && note?.private === true) base = { ...PRIVATE_ACCESS };
  return { ...base, inherited: true, owners: [note?.ownerUid, nb?.ownerUid].filter(Boolean) };
}

/** Is a viewer ({ uid, actsFor?, role, departmentIds }) in this access set? `owners` = uids always in. */
export function inAccess(access, viewer, owners = []) {
  const a = cleanAccess(access) ?? TEAM_ACCESS;
  const uids = [viewer?.uid, viewer?.actsFor].filter(Boolean);
  if (owners.some((o) => o && uids.includes(o))) return true;
  if (a.mode === 'team') return true;
  if (a.mode === 'private') return false;
  if (a.people.some((p) => uids.includes(p))) return true;
  if (viewer?.role && a.roles.includes(viewer.role)) return true;
  const depts = Array.isArray(viewer?.departmentIds) ? viewer.departmentIds : [];
  return a.departments.some((d) => depts.includes(d));
}

/** The fields a writer sends for an access change: the clean shape + the §3.7 mirror. Notebook → visibility; note → private. */
export function notebookAccessPatch(access) {
  const a = cleanAccess(access) ?? { ...TEAM_ACCESS };
  return { access: a, visibility: a.mode === 'private' ? 'private' : 'team' };
}
export function noteAccessPatch(access, nb) {
  const a = access == null ? null : cleanAccess(access);
  const resolved = a ?? notebookAccess(nb);
  return { access: a, private: resolved.mode === 'private' };
}

/** Same set? (order-free) */
export function sameAccess(a, b) {
  const x = cleanAccess(a) ?? TEAM_ACCESS; const y = cleanAccess(b) ?? TEAM_ACCESS;
  if (x.mode !== y.mode) return false;
  const eq = (p, q) => p.length === q.length && p.every((v) => q.includes(v));
  return eq(x.departments, y.departments) && eq(x.roles, y.roles) && eq(x.people, y.people);
}

/** The small mark a card / row carries (§3.10): 'custom' for a custom set (its own or its notebook's), 'private' for a note
 *  narrowed to private on its own (a private notebook already shows on the notebook), else null (team: nothing). */
export function accessGlyph(note, nb) {
  const r = resolveNoteAccess(note, nb);
  if (r.mode === 'custom') return 'custom';
  return !r.inherited && r.mode === 'private' && notebookAccess(nb).mode !== 'private' ? 'private' : null;
}

/** "admin" → "Admins", "team_lead" → "Team leads". */
export function roleLabel(role, plural = true) {
  const w = String(role || '').replace(/[_-]+/g, ' ').trim();
  if (!w) return '';
  const cap = w[0].toUpperCase() + w.slice(1);
  return plural ? (/s$/i.test(cap) ? cap : `${cap}s`) : cap;
}

/** The roles to offer: the org's members' roles, admin first, plus any already in the set. */
export function accessRoleChoices(members = [], access = null) {
  const seen = new Set(['admin']);
  for (const m of members) if (m?.role && !m.agent) seen.add(String(m.role));
  for (const r of cleanAccess(access)?.roles || []) seen.add(r);
  return ['admin', ...[...seen].filter((r) => r !== 'admin').sort()];
}

/**
 * One line for an access set: "Only you" · "Team" · "Field + Finance · admins · Pat" (custom; first names, "+2 more"
 * after three people). `departments` = [{ id, name }], `members` = [{ uid|id, name }], `myUid` names "you".
 */
export function accessSummary(access, { departments = [], members = [], myUid = null, ownerUid = null } = {}) {
  const a = cleanAccess(access) ?? TEAM_ACCESS;
  if (a.mode === 'team') return 'Team';
  if (a.mode === 'private') return !ownerUid || ownerUid === myUid ? 'Only you' : `Only ${firstName(members, ownerUid) || 'its owner'}`;
  const parts = [];
  const d = a.departments.map((id) => departments.find((x) => x.id === id)?.name).filter(Boolean);
  if (d.length) parts.push(d.join(' + '));
  if (a.roles.length) parts.push(a.roles.map((r) => roleLabel(r).toLowerCase()).join(' + '));
  const ppl = a.people.map((u) => (u === myUid ? 'you' : firstName(members, u))).filter(Boolean);
  if (ppl.length) parts.push(ppl.length > 3 ? `${ppl.slice(0, 3).join(', ')} +${ppl.length - 3} more` : ppl.join(', '));
  return parts.length ? parts.join(' · ') : 'Only you';
}
function firstName(members, uid) {
  const m = members.find((x) => (x?.uid || x?.id) === uid);
  return m?.name ? String(m.name).trim().split(/\s+/)[0] : null;
}

/** A dictated phrase into plain text at `at` (the cursor; default the end) — the editor's `dictate` rules: a capital
 *  to start a line or after a sentence, a space after a word. Returns the new text and where the cursor lands. */
export function insertDictated(text, phrase, at) {
  const src = String(text ?? '');
  let p = String(phrase ?? '').trim();
  const pos = Number.isInteger(at) && at >= 0 && at <= src.length ? at : src.length;
  if (!p) return { text: src, at: pos };
  const line = src.slice(0, pos).split('\n').pop();
  if (!line.trim() || /[.!?]\s*$/.test(line)) p = p[0].toUpperCase() + p.slice(1);
  if (line && !/\s$/.test(line)) p = ` ${p}`;
  return { text: src.slice(0, pos) + p + src.slice(pos), at: pos + p.length };
}
